import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { X, Upload, Download, Save, RotateCcw, MousePointerClick, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const FIELD_LABELS = {
  mnr: "Maßnahmenummer",
  mbez: "Maßnahmebezeichnung",
  md_beginn: "Maßnahmedauer – Beginn",
  md_ende: "Maßnahmedauer – Ende",
  anrede_name: "Frau / Herr + Name",
  tn_beginn: "Teilnahme – Beginn",
  tn_ende: "Teilnahme – Ende",
  ort_datum: "Ort und Datum",
  stempel: "Stempel + Unterschrift",
};
const FIELD_KEYS = Object.keys(FIELD_LABELS);
const TEXT_FIELD_KEYS = FIELD_KEYS.filter((k) => k !== "stempel");

export default function BgAusfertigungModal({ open, onClose, teilnehmer }) {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [values, setValues] = useState({});
  const [positions, setPositions] = useState({});
  const [hasScan, setHasScan] = useState(false);
  const [scanFilename, setScanFilename] = useState(null);
  const [kursId, setKursId] = useState(null);
  const [kursName, setKursName] = useState("");
  const [imgUrl, setImgUrl] = useState(null);
  const [imgNaturalSize, setImgNaturalSize] = useState({ w: 1, h: 1 });
  const [activeField, setActiveField] = useState(null);
  const [draggingField, setDraggingField] = useState(null);
  // Stempel-Bild (global für Träger)
  const [stempelUrl, setStempelUrl] = useState(null);
  const [stempelAspect, setStempelAspect] = useState(0.5); // h/w
  const [uploadingStempel, setUploadingStempel] = useState(false);

  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  const stempelInputRef = useRef(null);

  const teilnehmerId = teilnehmer?.id;

  // ---------- Load data on open ----------
  const loadData = useCallback(async () => {
    if (!teilnehmerId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/teilnehmer/${teilnehmerId}/bg-ausfertigung-data`);
      setValues(r.data.values || {});
      setPositions(r.data.positions || {});
      setHasScan(!!r.data.has_scan);
      setScanFilename(r.data.scan_filename || null);
      setKursId(r.data.kurs_id || null);
      setKursName(r.data.kurs_name || "");
      if (r.data.has_scan) {
        await loadScanImage();
      } else {
        setImgUrl(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Laden");
    }
    setLoading(false);
  }, [teilnehmerId]);

  const loadScanImage = useCallback(async () => {
    if (!teilnehmerId) return;
    try {
      const r = await axios.get(`${API}/teilnehmer/${teilnehmerId}/bg-scan-preview`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      setImgUrl(url);
    } catch (err) {
      // ignore
    }
  }, [teilnehmerId]);

  // Stempel-Bild laden (global)
  const loadStempelImage = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings/traeger-stempel`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setStempelUrl(url);
      // Aspect ratio ermitteln
      const img = new Image();
      img.onload = () => setStempelAspect(img.naturalHeight / img.naturalWidth);
      img.src = url;
    } catch (err) {
      setStempelUrl(null); // kein Stempel hinterlegt
    }
  }, []);

  const handleStempelUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploadingStempel(true);
    try {
      await axios.post(`${API}/settings/traeger-stempel`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Stempel + Unterschrift gespeichert (global)");
      if (stempelUrl) URL.revokeObjectURL(stempelUrl);
      await loadStempelImage();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    }
    setUploadingStempel(false);
  };

  useEffect(() => {
    if (open) {
      loadData();
      loadStempelImage();
    }
    return () => {
      if (imgUrl) URL.revokeObjectURL(imgUrl);
      if (stempelUrl) URL.revokeObjectURL(stempelUrl);
    };
    // eslint-disable-next-line
  }, [open, teilnehmerId]);

  // FIX: Radix Dialog Pointer-Events UND Focus-Scope-Trap umgehen.
  // Setzt alle Body-Kinder außer meinem Modal auf `inert` → Radix kann Fokus nicht
  // mehr auf die DialogContent zurückziehen, Inputs sind klickbar + fokussierbar.
  useEffect(() => {
    if (!open) return;
    const myModal = document.querySelector('[data-testid="bg-ausfertigung-modal"]');
    const prevBodyPE = document.body.style.pointerEvents;
    document.body.style.pointerEvents = "auto";

    const siblings = Array.from(document.body.children).filter((c) => c !== myModal && c.tagName !== "SCRIPT");
    const prevInert = siblings.map((s) => s.hasAttribute("inert"));
    siblings.forEach((s) => s.setAttribute("inert", ""));

    // Fokus auf das Modal setzen, um Radix' Rückziehen zu verhindern
    setTimeout(() => myModal?.focus?.(), 50);

    return () => {
      document.body.style.pointerEvents = prevBodyPE;
      siblings.forEach((s, i) => { if (!prevInert[i]) s.removeAttribute("inert"); });
    };
  }, [open]);

  // ---------- Upload scan ----------
  const handleUpload = async (file) => {
    if (!file || !teilnehmerId) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await axios.post(`${API}/teilnehmer/${teilnehmerId}/bg-scan-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Scan hochgeladen");
      await loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    }
    setUploading(false);
  };

  // ---------- Click on image to place active field ----------
  const handleImageClick = (e) => {
    if (!activeField || !imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setPositions((prev) => ({
      ...prev,
      [activeField]: {
        ...(prev[activeField] || { font_size: 10 }),
        x_pct: Math.max(0, Math.min(1, x)),
        y_pct: Math.max(0, Math.min(1, y)),
      },
    }));
    setActiveField(null);
  };

  // ---------- Drag existing marker ----------
  const handleMarkerMouseDown = (e, key) => {
    e.stopPropagation();
    setDraggingField(key);
  };
  useEffect(() => {
    if (!draggingField) return;
    const onMove = (e) => {
      if (!imgRef.current) return;
      const rect = imgRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      setPositions((prev) => ({
        ...prev,
        [draggingField]: {
          ...(prev[draggingField] || { font_size: 10 }),
          x_pct: Math.max(0, Math.min(1, x)),
          y_pct: Math.max(0, Math.min(1, y)),
        },
      }));
    };
    const onUp = () => setDraggingField(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [draggingField]);

  // ---------- Save positions per course ----------
  const savePositions = async () => {
    if (!kursId) return;
    try {
      await axios.post(`${API}/kurse/${kursId}/bg-feldpositionen`, { positions });
      toast.success("Positionen für diesen Kurs gespeichert");
    } catch (err) {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  // ---------- Generate PDF ----------
  const generatePdf = async () => {
    if (!teilnehmerId) return;
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/teilnehmer/${teilnehmerId}/bg-ausfertigung-pdf`,
        { values, positions, kurs_id: kursId },
        { responseType: "blob" }
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `BG-Ausfertigung_${teilnehmer?.nachname || "TN"}_${teilnehmer?.vorname || ""}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("BG-Ausfertigung erzeugt + bei Anhängen gespeichert ✓");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Generieren");
    }
    setGenerating(false);
  };

  if (!open) return null;

  // Portal direkt an document.body → Umgeht Radix-Dialog-Overlay-Stacking (z-50 der Elterndialoge)
  return createPortal(
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4"
      style={{ pointerEvents: "auto" }}
      tabIndex={-1}
      data-testid="bg-ausfertigung-modal"
    >
      {/* globales File-Input: beide Views (Upload + Editor) nutzen diese Instanz
          Trigger via <label htmlFor="bg-scan-file-input"> - iframe-sicher */}
      <input
        id="bg-scan-file-input"
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          handleUpload(f);
        }}
        data-testid="bg-scan-upload-input"
      />
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between text-white" style={{ background: 'linear-gradient(135deg, #D9775A 0%, #C2603F 100%)' }}>
          <div className="flex items-center gap-3">
            <FileText size={22} />
            <div>
              <h3 className="text-base font-semibold">BG-Ausfertigung ausfüllen</h3>
              <p className="text-xs opacity-90">
                {teilnehmer?.vorname} {teilnehmer?.nachname} · {kursName}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="bg-modal-close">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="animate-spin" size={28} />
          </div>
        ) : !hasScan ? (
          <div className="p-12 flex flex-col items-center justify-center text-center space-y-4">
            <Upload size={48} style={{ color: '#D9775A' }} />
            <h4 className="text-lg font-medium">BG-Scan hochladen</h4>
            <p className="text-sm text-gray-600 max-w-md">
              Lade den gescannten Bildungsgutschein (PDF) hoch, den du von der Agentur für Arbeit erhalten hast.
              Danach kannst du die Felder visuell platzieren.
            </p>
            <label
              htmlFor="bg-scan-file-input"
              className={`px-6 py-2 text-white rounded-lg flex items-center gap-2 cursor-pointer hover:opacity-90 transition ${uploading ? "opacity-50 pointer-events-none" : ""}`}
              style={{ background: '#D9775A' }}
              data-testid="bg-scan-upload-btn"
            >
              {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
              PDF auswählen
            </label>
          </div>
        ) : (
          <div className="flex flex-1 overflow-hidden">
            {/* LEFT: Scan with overlay */}
            <div className="flex-1 bg-gray-100 overflow-auto relative">
              <div
                className="relative mx-auto my-4 bg-white shadow-lg"
                style={{ width: "min(100%, 820px)", cursor: activeField ? "crosshair" : "default" }}
                onClick={handleImageClick}
              >
                {imgUrl && (
                  <img
                    ref={imgRef}
                    src={imgUrl}
                    alt="BG-Scan"
                    draggable={false}
                    onLoad={(e) =>
                      setImgNaturalSize({ w: e.target.naturalWidth, h: e.target.naturalHeight })
                    }
                    className="w-full h-auto select-none"
                    data-testid="bg-scan-image"
                  />
                )}
                {/* Field markers */}
                {TEXT_FIELD_KEYS.map((key) => {
                  const pos = positions[key];
                  if (!pos) return null;
                  const val = values[key] || "";
                  const isActive = activeField === key;
                  // Scale font size to approximate PDF→image mapping (image is 2x PDF)
                  const fontPx = (pos.font_size || 10) * 2 * (imgRef.current ? imgRef.current.clientWidth / imgNaturalSize.w : 1);
                  return (
                    <div
                      key={key}
                      onMouseDown={(e) => handleMarkerMouseDown(e, key)}
                      className={`absolute cursor-move whitespace-nowrap ${
                        isActive ? "text-blue-700 font-bold" : "text-black"
                      }`}
                      style={{
                        left: `${pos.x_pct * 100}%`,
                        top: `${pos.y_pct * 100}%`,
                        // WYSIWYG: Text-Baseline landet exakt bei y_pct — wie im PDF.
                        // Box 80 % nach oben verschieben, da Baseline ≈ 80 % der Boxhöhe.
                        transform: "translateY(-82%)",
                        fontSize: `${Math.max(8, fontPx)}px`,
                        fontFamily: "Helvetica, Arial, sans-serif",
                        lineHeight: 1,
                        userSelect: "none",
                        padding: "0 2px",
                        background: draggingField === key ? "rgba(255,210,0,0.5)" : "rgba(180,200,255,0.15)",
                        border: isActive ? "1px solid rgb(37, 99, 235)" : "1px dashed rgba(0,0,0,0.25)",
                      }}
                      data-testid={`bg-marker-${key}`}
                      title={`${FIELD_LABELS[key]} — zum Verschieben ziehen`}
                    >
                      {val || `[${FIELD_LABELS[key]}]`}
                      {/* kleine Hilfslinie, zeigt genau wo die Textbaseline sitzt */}
                      <span
                        style={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          bottom: "18%",
                          borderBottom: "1px solid rgba(37,99,235,0.6)",
                          pointerEvents: "none",
                        }}
                      />
                    </div>
                  );
                })}
                {/* Stempel+Unterschrift (Bild-Marker) */}
                {stempelUrl && positions.stempel && (
                  <div
                    onMouseDown={(e) => handleMarkerMouseDown(e, "stempel")}
                    className={`absolute cursor-move ${activeField === "stempel" ? "ring-2 ring-blue-500" : ""}`}
                    style={{
                      left: `${positions.stempel.x_pct * 100}%`,
                      top: `${positions.stempel.y_pct * 100}%`,
                      width: `${(positions.stempel.width_pct || 0.3) * 100}%`,
                      aspectRatio: `${1 / stempelAspect}`,
                      background: draggingField === "stempel" ? "rgba(255,210,0,0.2)" : "transparent",
                      border: activeField === "stempel" ? "1px solid rgb(37,99,235)" : "1px dashed rgba(0,0,0,0.25)",
                    }}
                    data-testid="bg-marker-stempel"
                    title="Stempel + Unterschrift — zum Verschieben ziehen"
                  >
                    <img
                      src={stempelUrl}
                      alt="Stempel"
                      draggable={false}
                      className="w-full h-full object-contain pointer-events-none select-none"
                    />
                  </div>
                )}
                {activeField && (
                  <div className="absolute top-2 left-2 right-2 bg-blue-600 text-white text-xs px-3 py-2 rounded shadow-lg flex items-center gap-2 pointer-events-none">
                    <MousePointerClick size={14} />
                    Klicke jetzt in den Scan wo <b>„{FIELD_LABELS[activeField]}"</b> stehen soll
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT: Field list */}
            <div className="w-96 border-l bg-white flex flex-col min-h-0">
              <div className="px-4 py-3 border-b" style={{ background: 'rgba(183,149,90,0.08)' }}>
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <MousePointerClick size={14} style={{ color: '#D9775A' }} />
                  So geht's
                </h4>
                <ol className="text-xs text-gray-700 mt-2 space-y-1 list-decimal list-inside">
                  <li>Werte prüfen & anpassen</li>
                  <li><b>Marker ziehen</b> oder <b>„Position setzen"</b> → in den Scan klicken</li>
                  <li>Passt? → <b>„Positionen für Kurs speichern"</b> (ab dann sitzen die Marker beim nächsten TN direkt richtig)</li>
                  <li><b>„BG-Ausfertigung herunterladen"</b></li>
                </ol>
                <p className="text-[10px] text-gray-500 mt-2 italic">
                  Nicht jeder Scan ist identisch — Positionen pro Kurs einmalig kalibrieren reicht.
                </p>
                <p className="text-[10px] text-gray-400 mt-1">Scan: {scanFilename}</p>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
                {/* Stempel + Unterschrift (global, einmalig) */}
                <div className="rounded-lg border-2 p-3" style={{ borderColor: "rgba(183,149,90,0.4)", background: "rgba(183,149,90,0.05)" }}>
                  <div className="text-xs font-semibold mb-1" style={{ color: "#8A6D3B" }}>
                    Stempel + Unterschrift (Dickies-Campus)
                  </div>
                  <div className="text-[10px] text-gray-500 italic mb-2">
                    Einmal für den Träger hinterlegen — wird auf JEDEM BG automatisch platziert.
                  </div>
                  {stempelUrl ? (
                    <div className="flex items-center gap-2">
                      <img src={stempelUrl} alt="Stempel" className="h-14 border rounded bg-white p-1 max-w-[120px] object-contain" />
                      <div className="flex-1 flex flex-col gap-1">
                        <label
                          htmlFor="bg-stempel-input"
                          className={`text-[11px] px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-center cursor-pointer ${uploadingStempel ? "opacity-50 pointer-events-none" : ""}`}
                        >
                          {uploadingStempel ? "lädt…" : "Ersetzen"}
                        </label>
                        <div className="flex gap-1">
                          <button
                            onClick={() => setActiveField(activeField === "stempel" ? null : "stempel")}
                            className={`flex-1 text-[11px] px-1 py-1 rounded ${activeField === "stempel" ? "bg-blue-600 text-white" : "bg-white border"}`}
                          >
                            {activeField === "stempel" ? "Abbrechen" : "Position"}
                          </button>
                          <input
                            type="range"
                            min="10"
                            max="50"
                            value={(positions.stempel?.width_pct || 0.3) * 100}
                            onChange={(e) => setPositions((p) => ({ ...p, stempel: { ...(p.stempel || {}), width_pct: parseInt(e.target.value) / 100 } }))}
                            className="flex-1"
                            title="Breite"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <label
                      htmlFor="bg-stempel-input"
                      className={`block w-full px-3 py-2 border-2 border-dashed rounded text-center text-sm cursor-pointer hover:bg-white ${uploadingStempel ? "opacity-50 pointer-events-none" : ""}`}
                      style={{ borderColor: "#D9775A", color: "#8A6D3B" }}
                    >
                      {uploadingStempel ? "lädt…" : "Stempel + Unterschrift hochladen (PNG/JPG)"}
                    </label>
                  )}
                  <input
                    id="bg-stempel-input"
                    ref={stempelInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      handleStempelUpload(f);
                    }}
                    data-testid="bg-stempel-upload-input"
                  />
                </div>

                {/* Gruppierung: einzelne + Beginn/Ende-Paare */}
                {[
                  { kind: "single", keys: ["mnr"] },
                  { kind: "single", keys: ["mbez"] },
                  { kind: "pair", title: "Maßnahmedauer (Zulassung)", subtitle: "Von wann bis wann ist die Maßnahme AZAV-zugelassen", keys: ["md_beginn", "md_ende"] },
                  { kind: "single", keys: ["anrede_name"] },
                  { kind: "pair", title: "Teilnahme (Kurs des TN)", subtitle: "Konkreter Kurszeitraum dieses Teilnehmers", keys: ["tn_beginn", "tn_ende"] },
                  { kind: "single", keys: ["ort_datum"] },
                ].map((group, gi) => {
                  const renderField = (key, extraLabel = null) => (
                    <div
                      key={key}
                      className={`border rounded-lg p-2 ${activeField === key ? "border-blue-500 bg-blue-50" : "border-gray-200"} flex-1`}
                    >
                      <label className="text-xs font-medium text-gray-700 block mb-1">
                        {extraLabel || FIELD_LABELS[key]}
                      </label>
                      <input
                        type="text"
                        value={values[key] || ""}
                        onChange={(e) =>
                          setValues((prev) => ({ ...prev, [key]: e.target.value }))
                        }
                        className="w-full text-sm border rounded px-2 py-1 focus:ring-1 focus:ring-amber-500 focus:outline-none"
                        data-testid={`bg-field-input-${key}`}
                      />
                      <div className="flex items-center gap-1 mt-2">
                        <button
                          onClick={() => setActiveField(activeField === key ? null : key)}
                          className={`text-[11px] px-2 py-1 rounded flex items-center gap-1 ${
                            activeField === key
                              ? "bg-blue-600 text-white"
                              : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                          }`}
                          data-testid={`bg-field-pos-${key}`}
                        >
                          <MousePointerClick size={11} />
                          {activeField === key ? "Abbrechen" : "Position"}
                        </button>
                        <select
                          value={positions[key]?.font_size || 10}
                          onChange={(e) =>
                            setPositions((prev) => ({
                              ...prev,
                              [key]: { ...(prev[key] || {}), font_size: parseInt(e.target.value) },
                            }))
                          }
                          className="text-[11px] border rounded px-1 py-1"
                          data-testid={`bg-field-size-${key}`}
                        >
                          {[8, 9, 10, 11, 12, 13, 14].map((s) => (
                            <option key={s} value={s}>{s}pt</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );

                  if (group.kind === "single") {
                    return <React.Fragment key={gi}>{renderField(group.keys[0])}</React.Fragment>;
                  }
                  // Paar-Gruppe mit Titel + Beginn/Ende nebeneinander
                  return (
                    <div
                      key={gi}
                      className="rounded-lg border-2 p-2"
                      style={{ borderColor: "rgba(183,149,90,0.4)", background: "rgba(183,149,90,0.05)" }}
                    >
                      <div className="mb-2">
                        <div className="text-xs font-semibold" style={{ color: "#8A6D3B" }}>
                          {group.title}
                        </div>
                        <div className="text-[10px] text-gray-500 italic">{group.subtitle}</div>
                      </div>
                      <div className="flex gap-2">
                        {renderField(group.keys[0], "Beginn")}
                        {renderField(group.keys[1], "Ende")}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t p-3 space-y-2 bg-gray-50">
                <button
                  onClick={savePositions}
                  className="w-full px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-sm rounded flex items-center justify-center gap-2"
                  data-testid="bg-save-positions-btn"
                >
                  <Save size={14} />
                  Positionen für Kurs „{kursName}" speichern
                </button>
                <label
                  htmlFor="bg-scan-file-input"
                  className={`w-full px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-sm rounded flex items-center justify-center gap-2 cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                  data-testid="bg-reupload-btn"
                >
                  {uploading ? <Loader2 className="animate-spin" size={14} /> : <RotateCcw size={14} />}
                  {uploading ? "Lade hoch…" : "Anderen Scan hochladen"}
                </label>
                <button
                  onClick={generatePdf}
                  disabled={generating}
                  className="w-full px-3 py-2 text-white text-sm rounded flex items-center justify-center gap-2 disabled:opacity-50 hover:opacity-90 transition"
                  style={{ background: '#D9775A' }}
                  data-testid="bg-generate-pdf-btn"
                >
                  {generating ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
                  BG-Ausfertigung herunterladen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
